import * as React from 'react'
import * as z from 'zod'
import {
  EnvelopeClosedIcon,
  InfoCircledIcon,
  MinusCircledIcon,
  PlusCircledIcon,
} from '@radix-ui/react-icons'
import { getId } from '@basehub/utils/id/index'
import { invariant } from '@epic-web/invariant'

import { useTeamReplicache } from '~/hooks-data/use-team-replicache'
import { useSelectedTeam } from '~/hooks-data/use-selected-team'
import { Button } from '~/sotano-kit/primitives/button'
import {
  DialogClose,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~/sotano-kit/primitives/dialog'
import LoadingIcon from '../../../../(repo)/[repo-slug]/(editor)/explore/components/blocks/empty-state/loading-icon'
import { IconButton } from '~/sotano-kit/primitives/icon-button'
import { LabeledInput } from '~/sotano-kit/primitives/input'
import { InviteLinkButton } from '../../../../(team)/settings/_components/invite'
// import {
//   Select,
//   SelectContent,
//   SelectItem,
//   SelectTrigger,
//   SelectValue,
// } from '~/sotano-kit/primitives/select'

// const MEMBER_TYPE = ['Member', 'Admin', 'View-only']

import type { OnboardingStep } from '.'

export type Invite = { email: string /* claims:  */ }

export const TeamOnboardingMembers: React.FC<
  Omit<OnboardingStep, 'onSkip'> & {
    loading?: boolean
    focusInput: boolean
    preselectedInvites: Array<Invite>
    onSkip: (e: React.MouseEvent, selectedRepos: Array<Invite>) => void
  }
> = ({ onSkip, focusInput, onNextStep, loading, preselectedInvites }) => {
  const replicache = useTeamReplicache().data?.replicache
  const { team } = useSelectedTeam()
  const [error, setError] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)
  // const [selectedClaim, setSelectedClaim] = React.useState<
  //   (typeof MEMBER_TYPE)[number]
  // >(MEMBER_TYPE[0])
  const [inviteList, setInviteList] =
    React.useState<Array<{ email: string }>>(preselectedInvites)

  React.useEffect(() => {
    if (!focusInput) return
    inputRef.current?.focus()
  }, [focusInput])

  return (
    <>
      <div>
        <DialogHeader className="pt-4 px-4 !space-y-1.5">
          <DialogTitle>Invite Team Members</DialogTitle>
          <DialogDescription>
            You can add unlimited members to your&nbsp;team.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            if (error) setError('')
            e.preventDefault()
            const formData = new FormData(e.currentTarget)
            const formEmail = formData.get('email') as string
            const formClaims = formData.get('claims') as string

            try {
              const { email } = z
                .object({
                  email: z
                    .string()
                    .email({ message: 'Please enter a valid email' }),
                  // claims: z.enum(['Member', 'Admin', 'View-only'], {
                  //   required_error: 'Please select a claim for this user',
                  // }),
                })
                .parse({ email: formEmail, claims: formClaims })

              const form = e.currentTarget
              setInviteList((prevState) => {
                if (
                  prevState?.some(
                    (invitedMember) => invitedMember.email === email
                  )
                ) {
                  setError('Email already added')
                  return prevState
                }
                const newState = [{ email }, ...prevState]

                form.reset()
                if (inputRef.current) inputRef?.current.focus()
                return newState
              })
            } catch (error) {
              if (error instanceof z.ZodError) {
                setError(error.issues?.[0]?.message ?? '')
              } else if (typeof error === 'string') {
                setError(error)
              }
              if (inputRef.current) inputRef?.current.focus()
            }
          }}
        >
          <div className="flex items-end">
            <LabeledInput
              className="!pt-6 !pb-2.5 !px-4 !pr-3 !-mx-0 flex-1"
              label="Add their email address"
              blocked={false}
              name="email"
              ref={inputRef}
              inputProps={{
                icon: <EnvelopeClosedIcon className="text-dark-muted" />,
                type: 'email',
                autoComplete: 'off',
                placeholder: 'Email address...',
              }}
            />
            {/* <Select
            name="claims"
            onValueChange={(value) => setSelectedClaim(value)}
            value={selectedClaim}
          >
            <SelectTrigger
              size="medium"
              className="h-7 mb-7 rounded-md !font-medium"
            >
              <SelectValue>{selectedClaim}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              {MEMBER_TYPE.map((s) => (
                <SelectItem value={s} key={s} className="text-xs">
                  <span>{s}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select> */}
          </div>
          <div className="px-4 pl-7 relative">
            <div className="w-[calc(100%-theme(spacing.10))] h-2 bg-gradient-to-b from-dark-shade to-transparent absolute -top-px" />
            <div className="w-[calc(100%-theme(spacing.10))] h-2 bg-gradient-to-t from-dark-shade to-transparent absolute -bottom-px" />

            <ul className="max-h-[220px] overflow-y-auto">
              {inviteList?.map((member, index) => (
                <div
                  key={member.email}
                  className="pb-2 last-of-type:pb-4 leading-tight w-full flex items-center justify-between text-sm text-dark-muted"
                >
                  <span className="text-ellipsis whitespace-nowrap overflow-hidden">
                    {member.email}
                  </span>
                  <IconButton
                    type="button"
                    className="ml-3 text-dark-muted"
                    onClick={() => {
                      setInviteList((prevState) => {
                        if (!prevState[index]) return prevState

                        const newState = [...prevState]
                        newState.splice(index, 1)
                        return newState
                      })
                    }}
                  >
                    <MinusCircledIcon />
                  </IconButton>
                </div>
              ))}
            </ul>
          </div>
          <div className="flex items-center justify-between mx-4 mb-6 mt-2">
            <Button
              type="submit"
              intent="secondary"
              size="large"
              icon={<PlusCircledIcon />}
            >
              Add Member
            </Button>
            <span className="text-xs text-dark-error">{error}</span>
          </div>
        </form>
      </div>

      <div>
        <div className="text-dark-muted px-4 pb-4 flex items-start space-x-2 text-sm leading-normal">
          <InfoCircledIcon className="flex-shrink-0 mt-1" />{' '}
          <p>New members will receive an invite through their&nbsp;email.</p>
        </div>
        <DialogFooter className="border-t text-dark-muted border-dark-border-solid px-4 py-4 flex items-center justify-between">
          <InviteLinkButton
            unstyled
            className="flex items-center text-sm mr-auto cursor-default hover:text-dark-dim gap-2"
            scope="team"
            role="member"
          />

          <div className="sm:space-x-4 flex items-center">
            <DialogClose asChild onClick={(e) => onSkip(e, inviteList)}>
              <Button intent="secondary" size="large" type="button">
                Back
              </Button>
            </DialogClose>
            <Button
              size="large"
              onClick={async (e) => {
                invariant(team, 'team was not found')

                if (inviteList.length > 0) {
                  replicache?.mutate.inviteToTeam({
                    teamId: team.id,
                    invite: inviteList.map(({ email }) => ({
                      token: getId(),
                      createdAt: new Date().toISOString(),
                      role: 'member',
                      hasAllAccess: true,
                      userIdLock: null,
                      emailLock: email,
                      useCount: 0,
                    })),
                  })
                }
                onNextStep(e)
              }}
              disabled={loading}
              icon={
                loading && (
                  <LoadingIcon status="loading" className="!text-inherit" />
                )
              }
            >
              {!loading ? 'Complete Setup' : 'Transferring Repos'}
            </Button>
          </div>
        </DialogFooter>
      </div>
    </>
  )
}
